"use client";

import { Suspense, useEffect, useMemo, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import axios from "axios";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { PageHeader } from "@/components/ui/PageHeader";
import { useAuthStore } from "@/store/auth";

/**
 * 📰 사장님 일상 — 4 분야 풀페이지 (Phase 2.0d, 2026-05-12).
 *
 * 탭:
 *   ☕ F&B 업계   (fnb_industry)
 *   📜 F&B 정책   (fnb_policy)
 *   💰 소상공인   (biz_support)
 *   🥛 식자재     (ingredient_price, 가락 vs 전국 토글)
 *
 * 비로그인도 노출. 로그인 시 알림 옵트인 안내 (Phase 2.5 후 활성).
 */

type CategoryKey = "fnb_industry" | "fnb_policy" | "biz_support" | "ingredient_price" | "subvention";

const TABS: { key: CategoryKey; label: string }[] = [
  { key: "fnb_industry", label: "F&B 업계" },
  { key: "fnb_policy", label: "F&B 정책" },
  { key: "biz_support", label: "소상공인" },
  { key: "ingredient_price", label: "식자재" },
  { key: "subvention", label: "정부지원금" },   // 사용자 결정 2026-05-15 — 마이그 073
];

interface NewsItem {
  id: number;
  category: string;
  category_label: string;
  sub_tag: string | null;
  title: string;
  summary: string;
  url: string;
  source: string | null;
  published_at: string | null;
  is_pinned: boolean;
}
interface NewsListOut {
  items: NewsItem[];
  total: number;
  has_more: boolean;
  sub_tag_counts: Record<string, number>;
}

// Step 3 (2026-05-12) — 분야별 sub-tag enum (백엔드와 동일)
const SUB_TAGS_BY_CATEGORY: Record<string, string[]> = {
  fnb_industry: ["신메뉴", "트렌드", "박람회", "시장 동향", "경쟁사"],
  fnb_policy: ["규제·법령", "회수·안전", "수수료", "인허가", "노무"],
  biz_support: ["지원금", "세제", "장학·복지", "정책", "교육"],
};

interface IngredientPoint {
  item_code: string;
  item_name: string;
  unit: string | null;
  region: string;
  price: number;
  price_date: string;
  change_vs_lastweek_pct: number | null;
  change_vs_lastmonth_pct: number | null;
  change_vs_lastyear_pct: number | null;
  // 마이그 063 (2026-05-13) — sparkline 시계열 + fallback 출처
  price_yesterday: number | null;
  price_lastweek: number | null;
  price_lastmonth: number | null;
  price_lastyear: number | null;
  price_source: string | null;
  // 2026-05-20 — KAMIS_ITEMS 의 peak 월 list. null = 연중. UI 시즌별 그룹 자동 분류용
  season: number[] | null;
}
interface IngredientSummaryOut {
  items: IngredientPoint[];
  as_of: string | null;
}

const PAGE_SIZE = 20;

function fmtDate(s: string | null): string {
  if (!s) return "";
  const d = new Date(s);
  // 2026-05-20 (사용자 결정) — 다른 년도면 "YYYY/MM/DD" 명시 (옛 가격 노출 시 사용자가 즉시 인지)
  if (d.getFullYear() !== new Date().getFullYear()) {
    return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
  }
  return `${d.getMonth() + 1}/${d.getDate()}`;
}
function fmtNumber(n: number): string {
  return n.toLocaleString();
}

function trackClick(newsId: number) {
  axios.post(`/api/news/${newsId}/click`).catch(() => {});
}

export default function NewsPage() {
  return (
    <Suspense fallback={<p className="text-center text-text-sub py-12">불러오는 중...</p>}>
      <NewsInner />
    </Suspense>
  );
}

function NewsInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const initialTab = (searchParams.get("tab") as CategoryKey) || "fnb_industry";
  const [tab, setTab] = useState<CategoryKey>(
    TABS.some((t) => t.key === initialTab) ? initialTab : "fnb_industry",
  );

  const changeTab = (next: CategoryKey) => {
    setTab(next);
    router.replace(`/news?tab=${next}`);
  };

  return (
    <div className="pb-24">
      <PageHeader title="📰 사장님 일상" backHref="/" />

      <div className="px-4 mt-3">
        <p className="text-2xs text-text-sub leading-relaxed">
          매일 자동 수집 — 카페 사장 관점 AI 요약. 카카오·네이버 뉴스에서 정보 찾는 시간 줄이세요
        </p>
      </div>

      {/* 4 탭 */}
      <div className="px-4 mt-3 flex gap-1.5 overflow-x-auto pb-1 hide-scrollbar">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => changeTab(t.key)}
            className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
              tab === t.key
                ? "bg-primary text-bg border-primary"
                : "border-border text-text-sub hover:text-text"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* 본문 — 분야별 */}
      <div className="px-4 mt-4">
        {tab === "ingredient_price" ? <IngredientPanel />
          : tab === "subvention" ? <SubventionPanel />
          : <NewsPanel category={tab} />}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 뉴스 3 분야 (텍스트)
// ─────────────────────────────────────────────────────────────
function NewsPanel({ category }: { category: CategoryKey }) {
  const [data, setData] = useState<NewsListOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [subTag, setSubTag] = useState<string | null>(null);

  // 분야 바뀌면 sub_tag 초기화
  useEffect(() => {
    setSubTag(null);
  }, [category]);

  useEffect(() => {
    setLoading(true);
    setPage(0);
    axios
      .get<NewsListOut>("/api/news/list", {
        params: { category, limit: PAGE_SIZE, offset: 0, sub_tag: subTag || undefined },
      })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [category, subTag]);

  const loadMore = async () => {
    if (!data) return;
    const next = page + 1;
    setLoading(true);
    try {
      const r = await axios.get<NewsListOut>("/api/news/list", {
        params: { category, limit: PAGE_SIZE, offset: next * PAGE_SIZE, sub_tag: subTag || undefined },
      });
      setData({ ...r.data, items: [...data.items, ...r.data.items] });
      setPage(next);
    } finally {
      setLoading(false);
    }
  };

  const allSubTags = SUB_TAGS_BY_CATEGORY[category] || [];
  const counts = data?.sub_tag_counts || {};

  return (
    <div className="space-y-2">
      {/* sub_tag 필터 칩 — Step 3 (2026-05-12) */}
      {allSubTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          <button
            onClick={() => setSubTag(null)}
            className={`text-2xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${
              subTag === null
                ? "bg-primary text-bg border-primary"
                : "border-border text-text-sub hover:text-text"
            }`}
          >
            전체
          </button>
          {allSubTags.map((tag) => {
            const n = counts[tag] || 0;
            const active = subTag === tag;
            return (
              <button
                key={tag}
                onClick={() => setSubTag(active ? null : tag)}
                disabled={n === 0 && !active}
                className={`text-2xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                  active
                    ? "bg-primary text-bg border-primary"
                    : n === 0
                      ? "border-border text-text-sub/40 cursor-not-allowed"
                      : "border-border text-text-sub hover:text-text"
                }`}
              >
                {tag}
                {n > 0 && (
                  <span className={`ml-1 ${active ? "text-bg/70" : "text-text-sub/60"}`}>{n}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {loading && !data ? (
        <p className="text-center text-text-sub py-12">불러오는 중...</p>
      ) : !data || data.items.length === 0 ? (
        <div className="text-center py-12 space-y-2">
          <p className="text-3xl">📭</p>
          <p className="text-sm text-text-sub">
            {subTag ? `'${subTag}' 분류 기사가 없어요` : "아직 수집된 뉴스가 없어요"}
          </p>
          {!subTag && <p className="text-2xs text-text-sub/70">매일 새벽 자동 수집됩니다</p>}
        </div>
      ) : (
        <>
          <p className="text-xs text-text-sub">
            {data.total}건{subTag && ` · '${subTag}'`}
          </p>
          {data.items.map((it) => (
            <a
              key={it.id}
              href={it.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClick(it.id)}
              className={`block card hover:border-primary/40 transition-colors ${
                it.is_pinned ? "border-primary/50 bg-primary/5" : ""
              }`}
            >
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                {it.is_pinned && <span className="text-2xs font-bold text-primary">⭐ 강조</span>}
                {it.sub_tag && (
                  <span className="text-2xs font-semibold text-primary/80 bg-primary/10 px-1.5 py-0.5 rounded">
                    {it.sub_tag}
                  </span>
                )}
                {it.source && <span className="text-2xs text-text-sub">{it.source}</span>}
                {it.published_at && (
                  <span className="text-2xs text-text-sub ml-auto">{fmtDate(it.published_at)}</span>
                )}
              </div>
              <p className="text-sm font-bold text-text leading-snug">{it.title}</p>
              <p className="text-2xs text-text-sub mt-1.5 leading-relaxed">{it.summary}</p>
            </a>
          ))}

          {data.has_more && (
            <button
              onClick={loadMore}
              disabled={loading}
              className="w-full mt-3 py-3 text-sm font-semibold text-text-sub border border-border rounded-2xl hover:bg-bg-elevated disabled:opacity-50"
            >
              {loading ? "불러오는 중..." : "더 보기"}
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 식자재 가격 (4 분야) — 2026-05-13 재설계
//   chip 선택 + 5주 도매 전국 평균 LineChart (manufacturer 차트 패턴)
//   가락/전국 동시 표시는 현재가 카드에서만 (KAMIS periodProductList 시장단위 시계열 미제공)
// ─────────────────────────────────────────────────────────────
interface HistoryPoint {
  date: string;
  price: number;
}
interface IngredientHistoryOut {
  item_code: string;
  item_name: string;
  region: string;
  unit: string | null;
  points: HistoryPoint[];
}

function IngredientPanel() {
  // 2026-05-14 — 사용자 결정: 가락 단일 (전국 평균 의미 X, 가락 70~80% 비중).
  const [summary, setSummary] = useState<IngredientSummaryOut | null>(null);
  const [selectedItem, setSelectedItem] = useState<string>("milk");
  const [history, setHistory] = useState<IngredientHistoryOut | null>(null);
  const [historyLoading, setHistoryLoading] = useState(true);
  // 2026-05-20 — 시즌 그룹 탭 (선택된 그룹의 chip 만 표시).
  // 초기 탭 = "지금 시즌" → 비어 있으면 "연중" 으로 자동 전환.
  const [activeSeasonTab, setActiveSeasonTab] = useState<SeasonGroupKey>("now");

  useEffect(() => {
    axios
      .get<IngredientSummaryOut>("/api/news/ingredient-summary", { params: { region: "가락" } })
      .then((r) => setSummary(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setHistoryLoading(true);
    axios
      .get<IngredientHistoryOut>("/api/news/ingredient-history", {
        params: { item: selectedItem, days: 70 },   // 사용자 결정 2026-05-15: 5주 → 10주
      })
      .then((r) => setHistory(r.data))
      .catch(() => setHistory(null))
      .finally(() => setHistoryLoading(false));
  }, [selectedItem]);

  // 2026-05-20 — chip 을 시즌 그룹별로 분류 (지금/다음/연중/옛 가격)
  const chipsBySeason = useMemo(() => {
    const groups: Record<SeasonGroupKey, IngredientPoint[]> = { now: [], next: [], yearround: [], off: [] };
    for (const it of summary?.items || []) {
      groups[classifyIngredientSeason(it)].push(it);
    }
    return groups;
  }, [summary]);

  const garakPoint = summary?.items.find((it) => it.item_code === selectedItem);

  const chartData = useMemo(() => {
    if (!history) return [];
    return history.points.map((p) => ({
      date: p.date.slice(5),
      price: p.price,
    }));
  }, [history]);

  // 1주 변동률 fallback — DB 의 change_vs_lastweek_pct 가 NULL 일 때.
  // history (가락 5주 시계열, DB 누적) 의 7일 전 가격으로 % 계산.
  const fallbackWeekPct = useMemo(() => {
    if (!history || history.points.length < 2) return null;
    const points = history.points;
    const last = points[points.length - 1];
    const target = new Date(last.date);
    target.setDate(target.getDate() - 7);
    const targetIso = target.toISOString().slice(0, 10);
    const prev = [...points].reverse().find((p) => p.date <= targetIso);
    if (!prev || prev.price <= 0) return null;
    return ((last.price - prev.price) / prev.price) * 100;
  }, [history]);

  return (
    <div>
      {/* 안내 박스 — 도매가 의미 + 활용법 */}
      <div className="card mb-3 bg-bg-elevated/50 border-primary/20">
        <p className="text-xs font-bold text-text mb-1.5">🥛 KAMIS 도매가란?</p>
        <p className="text-2xs text-text-sub leading-relaxed mb-2">
          가공·포장된 제품의 <b className="text-text">B2B 도매 거래 단가</b>입니다 (예: 우유 1L = 매일유업·서울우유 등 브랜드 통합). 시중 마트·식자재 마트 매입가에서 <b className="text-text">유통 마진을 뺀 가격</b>에 가깝습니다.
        </p>
        <p className="text-2xs font-bold text-primary mb-1">💡 활용법</p>
        <ul className="text-2xs text-text-sub space-y-0.5 leading-relaxed pl-3">
          <li>• 도매가 ↑ 5주 연속 = 매입가 인상 가능 (선행 지표)</li>
          <li>• 시즌 메뉴 기획 — 딸기 도매가 떨어지는 시점 포착</li>
          <li>• 공급처 협상 — &quot;도매가 X% 떨어졌는데 매입가 그대로?&quot;</li>
        </ul>
      </div>

      {/* 2026-05-20 (사용자 결정 두 번째) — 4 탭 + 선택 그룹 chip 만 표시.
          탭 카운트도 같이 ((5) 식) — 빈 그룹은 disabled. */}
      {summary && summary.items.length > 0 ? (
        <div className="mb-3">
          {/* 탭 4개 가로 */}
          <div className="flex gap-1 mb-2 border-b border-border">
            {SEASON_GROUP_DEFS.map((g) => {
              const count = chipsBySeason[g.key]?.length ?? 0;
              const isActive = activeSeasonTab === g.key;
              const isEmpty = count === 0;
              return (
                <button
                  key={g.key}
                  onClick={() => !isEmpty && setActiveSeasonTab(g.key)}
                  disabled={isEmpty}
                  className={`flex-1 px-2 py-1.5 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? "border-primary text-primary"
                      : isEmpty
                      ? "border-transparent text-text-sub/30 cursor-not-allowed"
                      : "border-transparent text-text-sub hover:text-text"
                  }`}
                >
                  {g.label} <span className="text-2xs font-normal opacity-70">({count})</span>
                </button>
              );
            })}
          </div>
          {/* 선택 탭의 chip 들 */}
          <div className="flex flex-wrap gap-1.5">
            {(chipsBySeason[activeSeasonTab] ?? []).map((it) => (
              <button
                key={it.item_code}
                onClick={() => setSelectedItem(it.item_code)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                  selectedItem === it.item_code
                    ? "bg-primary text-bg border-primary"
                    : "border-border text-text-sub hover:text-text"
                }`}
              >
                {it.item_name}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-2xs text-text-sub mb-3">품목 목록 불러오는 중...</p>
      )}

      {/* 선택 품목 현재가 — 한 줄 압축 (2026-05-14 사용자 결정) */}
      {garakPoint && <PriceInlineRow point={garakPoint} fallbackWeekPct={fallbackWeekPct} />}

      {/* 10주 가락 도매 라인차트 (사용자 결정 2026-05-15) */}
      <div className="card p-3">
        <div className="flex items-baseline justify-between mb-2">
          <p className="text-xs font-bold text-text">최근 10주 가격 추이</p>
          <p className="text-2xs text-text-sub">가락 도매</p>
        </div>
        <div className="h-60">
          {historyLoading && !history ? (
            <div className="h-full flex items-center justify-center">
              <p className="text-2xs text-text-sub">불러오는 중...</p>
            </div>
          ) : !history || history.points.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center gap-1">
              <p className="text-2xs text-text-sub">시계열 데이터가 없어요</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 2" stroke="rgba(255,255,255,0.06)" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: "#888" }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "#888" }}
                  tickFormatter={(v) => v.toLocaleString()}
                />
                <Tooltip
                  contentStyle={{
                    background: "#1a1a1a",
                    border: "1px solid #333",
                    borderRadius: 8,
                    fontSize: 11,
                  }}
                  formatter={(v: number) => [`${v.toLocaleString()}원`, "도매가"]}
                />
                <Line
                  type="monotone"
                  dataKey="price"
                  stroke="#FF5C35"
                  strokeWidth={1.8}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* 주차별 표 (사용자 결정 2026-05-15) — 직관적 숫자 확인 */}
        {history && history.points.length > 0 && (() => {
          // 일별 points → 주차 그룹 (각 주의 마지막 일자 가격)
          const sorted = [...history.points].sort((a, b) => a.date.localeCompare(b.date));
          const byWeek: Record<string, { date: string; price: number }> = {};
          for (const p of sorted) {
            const d = new Date(p.date);
            const day = d.getDay();
            const diff = day === 0 ? -6 : 1 - day;   // 월요일 시작
            const monday = new Date(d);
            monday.setDate(d.getDate() + diff);
            const wkKey = monday.toISOString().slice(0, 10);
            byWeek[wkKey] = { date: p.date, price: p.price };  // 주중 마지막 가격으로 덮어씀
          }
          const weekly = Object.entries(byWeek)
            .sort(([a], [b]) => b.localeCompare(a))   // 최근 주 먼저
            .slice(0, 10);
          if (weekly.length === 0) return null;
          // 변동률 계산 (이전 주 대비)
          const prices = weekly.map(([, v]) => v.price);
          return (
            <div className="mt-3 border-t border-border pt-3">
              <p className="text-2xs text-text-sub mb-1.5">주차별 마지막 가격</p>
              <div className="overflow-x-auto">
                <table className="w-full text-2xs">
                  <thead className="text-text-sub">
                    <tr>
                      <th className="px-1 py-1 text-left font-medium">주차 (월요일)</th>
                      <th className="px-1 py-1 text-left font-medium">기준일</th>
                      <th className="px-1 py-1 text-right font-medium">가격</th>
                      <th className="px-1 py-1 text-right font-medium">전주비</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weekly.map(([wkKey, v], idx) => {
                      const prev = idx + 1 < weekly.length ? prices[idx + 1] : null;
                      const ch = prev != null && prev > 0 ? ((v.price - prev) / prev) * 100 : null;
                      const chColor = ch == null ? "text-text-sub" : ch > 0 ? "text-red-400" : ch < 0 ? "text-emerald-500" : "text-text-sub";
                      const chText = ch == null ? "—" : ch > 0 ? `↑${ch.toFixed(1)}%` : ch < 0 ? `↓${Math.abs(ch).toFixed(1)}%` : "±0%";
                      return (
                        <tr key={wkKey} className="border-t border-border/40">
                          <td className="px-1 py-1 text-text">{wkKey.slice(5)}</td>
                          <td className="px-1 py-1 text-text-sub">{v.date.slice(5)}</td>
                          <td className="px-1 py-1 text-right text-text font-semibold">{v.price.toLocaleString()}원</td>
                          <td className={`px-1 py-1 text-right font-semibold ${chColor}`}>{chText}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// 2026-05-20 (사용자 결정 — 두 번째 검토) — 4 탭 + 셀렉트 패턴, 이모지 제거.
type SeasonGroupKey = "now" | "next" | "yearround" | "off";

interface SeasonGroup {
  key: SeasonGroupKey;
  label: string;
}

const SEASON_GROUP_DEFS: SeasonGroup[] = [
  { key: "now",       label: "지금 시즌" },
  { key: "next",      label: "다음 시즌" },
  { key: "yearround", label: "연중" },
  { key: "off",       label: "옛 가격" },
];

/**
 * 현재 월 기준 시즌 그룹 분류.
 * - season list 에 (현재 월) 또는 (현재 월 ±1) 포함 → now
 * - season list 에 (2~3 개월 후) 포함 → next
 * - season === null → yearround
 * - 그 외 → off (마지막 거래 가격 표시 대상)
 */
function classifyIngredientSeason(p: IngredientPoint): SeasonGroupKey {
  if (!p.season || p.season.length === 0) return "yearround";
  const month = new Date().getMonth() + 1;
  const prevM = month === 1 ? 12 : month - 1;
  const nextM = month === 12 ? 1 : month + 1;
  if (p.season.includes(month) || p.season.includes(prevM) || p.season.includes(nextM)) return "now";
  for (let i = 2; i <= 3; i++) {
    const m = ((month - 1 + i) % 12) + 1;
    if (p.season.includes(m)) return "next";
  }
  return "off";
}

function PriceInlineRow({
  point, fallbackWeekPct,
}: {
  point: IngredientPoint;
  fallbackWeekPct: number | null;
}) {
  const rawCh = point.change_vs_lastweek_pct;
  const ch = rawCh ?? fallbackWeekPct;
  const isFallback = rawCh === null && fallbackWeekPct !== null;
  const chColor =
    ch === null ? "text-text-sub" : ch > 0 ? "text-red-400" : ch < 0 ? "text-emerald-500" : "text-text-sub";
  const chText =
    ch === null
      ? ""
      : ch > 0
        ? `↑${ch.toFixed(1)}%`
        : ch < 0
          ? `↓${Math.abs(ch).toFixed(1)}%`
          : "±0%";
  const isStale = point.price_source && point.price_source !== "당일";

  return (
    <div className={`mb-3 rounded-lg px-3 py-2 flex items-center gap-2 flex-wrap text-xs ${
      isStale ? "bg-yellow-400/5 border border-yellow-400/30" : "bg-bg-elevated/40 border border-border"
    }`}>
      <span className="font-bold text-text">{point.item_name}</span>
      {point.unit && <span className="text-text-sub text-2xs">{point.unit}</span>}
      <span className="text-text-sub">· 가락 (서울)</span>
      <span className="text-base font-black text-text ml-auto">
        {fmtNumber(point.price)}<span className="text-2xs font-normal text-text-sub ml-0.5">원</span>
      </span>
      {chText && (
        <span className={`text-2xs font-semibold ${chColor}`}>
          {chText}
          <span className="text-text-sub font-normal ml-0.5">지난주{isFallback ? "*" : ""}</span>
        </span>
      )}
      <span className="text-2xs text-text-sub">{fmtDate(point.price_date)}</span>
      {isStale && (
        <span className="w-full text-2xs text-yellow-500 mt-0.5">⚠ {point.price_source} 데이터</span>
      )}
      {isFallback && (
        <span className="w-full text-2xs text-text-sub mt-0.5">
          * 지난주 변동률 = 5주 시계열 7일 전 기준 (KAMIS 1주전 값 누락 fallback)
        </span>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 정부지원금 패널 (마이그 073, 2026-05-15) — /subvention 통합 이전
// 카드 UI = 사진 참고: 카테고리 라벨 + 타이틀 + 기간/지원혜택/지역/지원처만
// ─────────────────────────────────────────────────────────────
interface SubventionRow {
  id: number;
  title: string;
  summary: string | null;
  organization: string | null;
  target: string | null;
  region: string | null;
  industry: string | null;
  support_type: string | null;
  support_amount: string | null;
  apply_start: string | null;
  apply_end: string | null;
  apply_url: string | null;
  days_left: number | null;
  match_score: number;
  match_reasons?: string[];   // Phase 2 (사용자 결정 2026-05-15) — 매칭 사유 표시
}
interface SubventionListOut {
  items: SubventionRow[];
  total: number;
  has_store_match: boolean;
  user_region: string | null;
}

const SUPPORT_TYPE_LABEL_SV: Record<string, string> = {
  fund: "자금", tax: "세제", facility: "시설", education: "교육",
  consulting: "컨설팅", other: "기타",
};

function SubventionPanel() {
  // 2026-05-15 — 데이터 누적 부족으로 안내만 (사용자 결정)
  // 풀 활성화 시점에 본 컴포넌트 git history 에서 복원 (마이그 073 인프라 그대로)
  return (
    <div className="text-center py-12 px-4">
      <p className="text-sm font-bold text-text mb-2">데이터 취합 중</p>
      <p className="text-2xs text-text-sub leading-relaxed">
        카페·외식업에 맞는 정부지원금을 모으고 있어요.
        <br />
        조금 더 풍부해지면 매장에 자동 매칭해서 알려드릴게요.
      </p>
    </div>
  );
}

function SubventionCard({ row }: { row: SubventionRow }) {
  const dl = row.days_left;
  const dlBadge = dl == null ? "상시" : `D-${dl}`;
  const dlColor =
    dl == null ? "bg-bg-elevated text-text-sub"
      : dl <= 7 ? "bg-red-400/15 text-red-400"
      : dl <= 30 ? "bg-yellow-400/15 text-yellow-400"
      : "bg-bg-elevated text-text-sub";
  const category = row.support_type ? (SUPPORT_TYPE_LABEL_SV[row.support_type] || row.support_type) : "기타";

  const inner = (
    <div className="card p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-2xs font-bold text-primary">{category}</span>
        <span className={`text-2xs font-bold px-2 py-0.5 rounded ${dlColor}`}>{dlBadge}</span>
      </div>
      <p className="text-sm font-bold text-text leading-tight line-clamp-2 mb-2">{row.title}</p>
      <div className="space-y-0.5 text-2xs">
        <div className="flex gap-2">
          <span className="text-text-sub w-14 shrink-0">기간</span>
          <span className="text-text">
            {row.apply_start && row.apply_end
              ? `${row.apply_start} ~ ${row.apply_end}`
              : row.apply_end ? `~${row.apply_end}` : "상시 접수"}
          </span>
        </div>
        {row.support_amount && row.support_amount !== "공고 참조" && (
          <div className="flex gap-2">
            <span className="text-text-sub w-14 shrink-0">지원혜택</span>
            <span className="text-text">{row.support_amount}</span>
          </div>
        )}
        <div className="flex gap-2">
          <span className="text-text-sub w-14 shrink-0">지역</span>
          <span className="text-text">{row.region ?? "전국"}</span>
        </div>
        {row.organization && (
          <div className="flex gap-2">
            <span className="text-text-sub w-14 shrink-0">지원처</span>
            <span className="text-text truncate">{row.organization}</span>
          </div>
        )}
      </div>
      {row.match_reasons && row.match_reasons.length > 0 && (
        <div className="mt-2 pt-2 border-t border-border/40 flex flex-wrap gap-1">
          {row.match_reasons.map((r, i) => (
            <span key={i} className="text-2xs text-primary bg-primary/10 px-1.5 py-0.5 rounded">
              ✓ {r}
            </span>
          ))}
        </div>
      )}
    </div>
  );

  if (!row.apply_url) return inner;
  return (
    <a href={row.apply_url} target="_blank" rel="noopener noreferrer" className="block hover:opacity-90">
      {inner}
    </a>
  );
}
